import { get_ai_download_url } from 'services/db-service';
import { backendFetch } from 'services/backend-auth';
import VideoSourceMenu from './VideoSourceMenu';
import VideoVolumeControl from './VideoVolumeControl';
import ShellIcon from './ShellIcon';
import { mediaGateways, preferredGateway, mediaUrl, parseMediaReference, useMediaConfig, ipfsAvailability } from 'services/media';
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import VoteButton from "./VoteButton";
import VideoReplies from "./VideoReplies";
import { useDispatch, useSelector } from "react-redux";
import { fetchReplies } from "features/posts/repliesSlice";
import { RootState } from "app/store/rootReducer";
import RewardsButton from "./RewardsButton";
import { extractIPFSLink, getTimeAgo, getTimeToExpiration, renderHTMLSafe } from "services/posts-service";
import { shareKeychain } from 'helpers/keychain';
import { shareWithWif } from 'helpers/steem';
import { isLoggedWithKeychain } from 'services/dsteem-service';
import ReplyEditor from "features/posts/replyEditor";
import { get_plans, get_views, register_view, skip_video, get_ipfs_metadata, update_ipfs_metadata, aiBackend, download_video } from "services/db-service";
import { useAuth } from "helpers/authContext";
import { Spinner } from 'reactstrap';
import classnames from 'classnames';
import { extractBodySummary } from "helpers/contentRenderer";
import renderer, { replaceIPFSVideoLinks } from "helpers/contentRenderer";
import FollowButton from "./FollowButton";
import { useToast } from "./ui/use-toast";
import { VerifiedIcon } from "./VerifiedIcon";
import ContentEditPostModal from "features/content/contentModals/ContentEditPostModal";
export default function VideoFeed({
  posts: initialPosts = [],
  fetchMore,
  hasMore = false,
  toggleLoginModal,
  localReplies,
  setLocalReplies
}: {
  posts: any[];
  fetchMore?: () => Promise<any[]>;
  hasMore?: boolean;
  toggleLoginModal: any;
  localReplies: any;
  setLocalReplies: any;
}) {
  const [posts, setPosts] = useState<any[]>(initialPosts);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState('');
  const pagingBusy = useRef(false);
  const feedGeneration = useRef(0);
  const loadNextPage = React.useCallback(async () => {
    if (!fetchMore || !hasMore || pagingBusy.current) return false;
    const current = feedGeneration.current;
    pagingBusy.current = true;
    setLoadingMore(true);
    setMoreError('');
    try {
      const more = await fetchMore();
      if (current !== feedGeneration.current) return false;
      setPosts(previous => {
        const seen = new Set(previous.map(post => post.author + '/' + post.permlink));
        return [...previous, ...more.filter(post => {
          const key = post.author + '/' + post.permlink;
          if (seen.has(key)) return false;
          seen.add(key); return true;
        })];
      });
      return more.length > 0;
    } catch {
      if (current === feedGeneration.current) setMoreError('Could not load more videos.');
      return false;
    } finally {
      if (current === feedGeneration.current) { pagingBusy.current = false; setLoadingMore(false); }
    }
  }, [fetchMore, hasMore]);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [volume, setVolume] = useState(1);
  const pendingSourceSeek = useRef<{ cid: string; time: number } | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState("");
  const [views, setViews] = useState([]);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef<HTMLDivElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userPlans, setUserPlans] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [viewRegistered, setViewRegistered] = useState(false);
  const { user, autologin, recently_viewed, updateRecentlyViewed, app_settings, updateAppSettings } = useAuth();
  const mediaConfiguration = useMediaConfig();
  const BASE_GATEWAYS = mediaGateways();
  const [cacheBypass, setCacheBypass] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [showComments, setShowComments] = useState<boolean>(false);
  const [closing, setClosing] = useState<boolean>(false);
  const [showPostSheet, setShowPostSheet] = useState<boolean>(false);
  const [closingPostSheet, setClosingPostSheet] = useState<boolean>(false);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubTimeText, setScrubTimeText] = useState("");
  const [showOptionsSheet, setShowOptionsSheet] = useState<boolean>(false);
  const [closingOptionsSheet, setClosingOptionsSheet] = useState<boolean>(false);
  // Share inline state (inside options sheet)
  const [showShareConfirm, setShowShareConfirm] = useState(false);
  const [isSharingVideo, setIsSharingVideo] = useState(false);
  const [hasSharedVideo, setHasSharedVideo] = useState(false);
  const isScrubbingRef = useRef<boolean>(false);
  const cumulativeWatchTimeRef = useRef<number>(0);
  const lastTimeUpdateRef = useRef<number>(0);
  const isSwipingRef = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const stalledTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref mirrors activeIndex so IntersectionObserver closure never goes stale
  const activeIndexRef = useRef<number>(0);
  const dispatch = useDispatch();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [postSheetTab, setPostSheetTab] = useState('post');
  const [aiTranscription, setAiTranscription] = useState<string | null>(null);
  const [aiLanguage, setAiLanguage] = useState<string | null>(null);
  const [aiSrt, setAiSrt] = useState<string | null>(null);
  const [aiSrtEn, setAiSrtEn] = useState<string | null>(null);
  const [activeSubTrack, setActiveSubTrack] = useState<'original' | 'en'>('original');
  const [aiLanguageLabel, setAiLanguageLabel] = useState<string>('');
  const [transcriptionViewMode, setTranscriptionViewMode] = useState<'text' | 'srt'>('text');
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiStatus, setAiStatus] = useState('');
  const [showSubtitles, setShowSubtitles] = useState<boolean>(true);
  const [currentSubtitle, setCurrentSubtitle] = useState<string>("");
  const [parsedCues, setParsedCues] = useState<{start: number, end: number, text: string}[]>([]);

  const LANG_MAP: Record<string, string> = {
    "en": "English", "es": "Spanish", "fr": "French", "de": "German", 
    "it": "Italian", "pt": "Portuguese", "nl": "Dutch", "ru": "Russian",
    "zh": "Chinese", "ja": "Japanese", "ko": "Korean", "ar": "Arabic",
    "hi": "Hindi", "tr": "Turkish", "vi": "Vietnamese", "pl": "Polish",
    "uk": "Ukrainian", "id": "Indonesian", "ms": "Malay", "th": "Thai"
  };

  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchEndY = useRef<number | null>(null);

  const handleSwipeStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchStartY.current = e.targetTouches[0].clientY;
    touchEndX.current = null;
    touchEndY.current = null;
  };
  const handleSwipeMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
    touchEndY.current = e.targetTouches[0].clientY;
  };
  const handleSwipeEnd = (author: string) => {
    if (touchStartX.current === null || touchEndX.current === null || touchStartY.current === null || touchEndY.current === null) return;
    const distanceX = touchStartX.current - touchEndX.current;
    const distanceY = touchStartY.current - touchEndY.current;
    const threshold = 75; // 75px horizontal requirement to trigger action
    
    // Check if the scroll was primarily horizontal vs vertical
    if (Math.abs(distanceX) > Math.abs(distanceY)) {
      isSwipingRef.current = true;
      setTimeout(() => { isSwipingRef.current = false; }, 500); // absorb trailing clicks
      if (distanceX > threshold) {
        // Swiped Left
        setClosing(false);
        setShowComments(true);
      } else if (distanceX < -threshold) {
        // Swiped Right
        navigate(`/u/${author}`);
      }
    }
    
    // Reset refs after swipe completion just to be safe
    touchStartX.current = null;
    touchEndX.current = null;
    touchStartY.current = null;
    touchEndY.current = null;
  };

  const getCIDPath = (url: unknown) => parseMediaReference(url);
  const [gateway, setGateway] = useState(() => preferredGateway(app_settings?.gateway));

  const post = posts[activeIndex];
  const activeMetadata = (() => {
    try {
      return (typeof post?.json_metadata !== "string" || !post.json_metadata || post.json_metadata.trim() === "undefined") ? {} : JSON.parse(post.json_metadata || "{}");
    } catch (e) {
      return {};
    }
  })();
  const activeUrl = extractIPFSLink(post?.body || "", activeMetadata) || activeMetadata?.video;
  const activeCidPath = getCIDPath(activeUrl);
  const failedGatewaysRef = useRef<string[]>([]);

  const dynamicGateways = BASE_GATEWAYS;
  const activeFinalUrl = mediaUrl(activeUrl, gateway, !cacheBypass);

  const selectSource = (source: string) => {
    if (source === preferredGateway(gateway)) return;
    const time = videoRef.current?.currentTime || 0;
    if (activeCidPath?.cid && Number.isFinite(time)) pendingSourceSeek.current = { cid: activeCidPath.cid, time };
    setGateway(source);
    setCacheBypass(false);
    failedGatewaysRef.current = [];
    updateAppSettings({ ...app_settings, gateway: source });
  };
  useEffect(() => {
    if (videoRef.current) { videoRef.current.volume = volume; videoRef.current.muted = isMuted; }
  }, [volume, isMuted, activeIndex, activeUrl]);

  const safePlay = async (video: HTMLVideoElement) => {
    const source = video.currentSrc || video.src;
    try {
      await video.play();
      if (videoRef.current !== video || (video.currentSrc || video.src) !== source) return;
      setIsPlaying(true);
      setIsLoading(false);
    } catch (error) {
      if (videoRef.current !== video || (video.currentSrc || video.src) !== source) return;
      // A rejected autoplay must leave manual playback available.
      if (error.name !== 'AbortError') { setIsPlaying(false); setIsLoading(false); }
    }
  };

  useEffect(() => {
    feedGeneration.current += 1;
    pagingBusy.current = false;
    setLoadingMore(false);
    setMoreError('');
    setPosts(initialPosts);
    setActiveIndex(index => Math.max(0, Math.min(index, initialPosts.length - 1)));
  }, [initialPosts]);
  useEffect(() => {
    if (posts.length && activeIndex >= posts.length - 3 && !moreError) void loadNextPage();
  }, [activeIndex, posts.length, loadNextPage, moreError]);
  useEffect(() => () => { feedGeneration.current += 1; }, []);
  useEffect(() => {
    const selected = preferredGateway(app_settings?.gateway);
    if (selected && app_settings?.gateway !== selected) updateAppSettings({ ...app_settings, gateway: selected });
    setGateway(selected);
  }, [app_settings?.gateway, mediaConfiguration]);

  useEffect(() => {
    failedGatewaysRef.current = [];
  }, [activeIndex, activeUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !activeFinalUrl) return;
    let cancelled = false;
    setIsPlaying(false);
    setIsLoading(true);
    setHasError(false);
    setErrorMessage('');
    const onReady = () => { if (!cancelled) void safePlay(video); };
    if (video.readyState >= 3) onReady();
    else video.addEventListener('canplay', onReady, { once: true });
    return () => {
      cancelled = true;
      video.removeEventListener('canplay', onReady);
      video.pause();
      if (stalledTimerRef.current) clearTimeout(stalledTimerRef.current);
      // React owns src. Removing it here breaks retained items when scrolling back.
    };
  }, [activeIndex, activeFinalUrl]);

  // Keep the ref in sync with state so observer callbacks always see the latest value
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  // Stall recovery: if video freezes network-side, force a reload after 8s
  const handleStalled = () => {
    if (stalledTimerRef.current) clearTimeout(stalledTimerRef.current);
    stalledTimerRef.current = setTimeout(() => {
      console.log("[VideoFeed] Video stalled — let's wait a bit more...");
      // Removed .load() to prevent restart from 0:00 on slow connections
    }, 8000);
  };

  // Use IntersectionObserver to update activeIndex on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute("data-index") || "0");
            // Use activeIndexRef (always current) to avoid stale closure
            if (index !== activeIndexRef.current) {
              setActiveIndex(index);
              activeIndexRef.current = index;
              setLocalReplies([]);
              // Clear any pending stall timer when switching videos
              if (stalledTimerRef.current) clearTimeout(stalledTimerRef.current);
            }
          }
        });
      },
      {
        threshold: 0.5,
      }
    );

    itemRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  // activeIndex intentionally omitted — using activeIndexRef instead
  }, [posts.length]);

  const scrollToVideo = (index: number) => {
    const target = itemRefs.current[index];
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };
  useEffect(() => {
    const getViews = async () => {
      let data = await get_views();
      setViews(data);
    }
    getViews();
    const getPlans = async () => {
      let data = await get_plans();
      setUserPlans(data);
    }
    getPlans();
  }, []);
  const handleNext = async (manualSkip = false) => {
    if (showComments || showPostSheet) return;

    const currentPost = posts[activeIndex];
    const shouldSkip = manualSkip || (!isLoading && !hasError);

    if (user && currentPost?.author && currentPost?.permlink && shouldSkip) {
      skip_video(user, currentPost.author, currentPost.permlink);
    }

    const nextIndex = Math.min(activeIndex + 1, posts.length - 1);
    if (nextIndex !== activeIndex) {
      scrollToVideo(nextIndex);
    } else if (hasMore && await loadNextPage()) {
      requestAnimationFrame(() => scrollToVideo(activeIndex + 1));
    }
  };

  const handlePrev = () => {
    if (showComments || showPostSheet) return;
    const prevIndex = Math.max(activeIndex - 1, 0);
    if (prevIndex !== activeIndex) {
      scrollToVideo(prevIndex);
    }
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((err) => {
            if (err.name === 'AbortError') {
              console.log("⏯ Play interrupted safely.");
            } else {
              console.error("Play failed", err);
            }
          });
      }
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };
  const toggleMute = () => {
    setIsMuted((prev) => !prev);
  };



  const openComments = () => {
    setClosing(false);
    setShowComments(true);
  };

  const closeComments = () => {
    setClosing(true);
    setTimeout(() => setShowComments(false), 300);
  };

  const openPostSheet = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setClosingPostSheet(false);
    setShowPostSheet(true);
  };

  const closePostSheet = () => {
    setClosingPostSheet(true);
    setTimeout(() => setShowPostSheet(false), 300);
  };

  const openMoreOptions = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setClosingOptionsSheet(false);
    setShowOptionsSheet(true);
  };

  const closeMoreOptions = () => {
    setClosingOptionsSheet(true);
    setTimeout(() => setShowOptionsSheet(false), 300);
    setShowShareConfirm(false); // reset inner share confirm on close
  };

  const handleInlineShare = async () => {
    if (!user) { toggleLoginModal(); return; }
    if (hasSharedVideo) {
      toast({ title: "Already shared!", description: "You already shared this video.", variant: "rose_red" });
      return;
    }
    setIsSharingVideo(true);
    try {
      const keychainLogin = isLoggedWithKeychain(autologin);
      const voteFn = keychainLogin ? shareKeychain : shareWithWif;
      const account = keychainLogin ? user : autologin;
      const result: any = await voteFn(account, post.author, post.permlink);
      if (!result.success) {
        toast({ title: "Error sharing", description: result.result?.message, variant: "rose_red" });
      } else {
        setHasSharedVideo(true);
        toast({ title: "Video shared!", description: "Find it in your profile.", variant: "sky_blue" });
        setShowShareConfirm(false);
        closeMoreOptions();
      }
    } catch (e: any) {
      toast({ title: "Error sharing", description: e?.message, variant: "rose_red" });
    }
    setIsSharingVideo(false);
  };

  const handleCopyLink = () => {
    const postUrl = `${window.location.origin}/p/${post.author}/${post.permlink}`;
    navigator.clipboard.writeText(postUrl);
    toast({ title: "Link copied!", description: "URL copied to clipboard.", variant: "sky_blue" });
  };

  const generateTranscriptionFromIPFS = async () => {
    if (!activeCidPath?.cid || !user) return;
    setAiProcessing(true);
    setAiStatus('Downloading IPFS object...');
    
    try {
        const res = await backendFetch(`${aiBackend}/api/v1/process_ipfs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                cid: activeCidPath.cid,
                username: user,
 
            })
        });
        const data = await res.json();
        
        if (data.task_id) {
            setAiStatus('Generating...');
            const interval = setInterval(async () => {
                try {
                    const poll = await backendFetch(`${aiBackend}/api/v1/task/${data.task_id}`);
                    const pollData = await poll.json();
                    if (pollData.status === 'completed') {
                        setAiStatus('');
                        setAiProcessing(false);
                        const result = pollData.result;
                        setAiTranscription(result.transcription);
                        if (result.language) {
                            setAiLanguage(result.language);
                            setAiLanguageLabel(LANG_MAP[result.language] || result.language.toUpperCase());
                        }
                        if (result.srt_content) setAiSrt(result.srt_content);
                        if (result.srt_content_en) setAiSrtEn(result.srt_content_en);
                        
                        // Save both original and English versions
                        update_ipfs_metadata(
                            activeCidPath.cid, 
                            result.ai_metadata, 
                            result.transcription, 
                            user, 
                            result.language, 
                            result.srt_content,
                            result.transcription_en,
                            result.srt_content_en
                        );
                        clearInterval(interval);
                    } else if (pollData.status === 'failed') {
                        setAiStatus('');
                        setAiProcessing(false);
                        toast({title: "Error", description: pollData.error || "AI task failed", variant: "default" });
                        clearInterval(interval);
                    } else {
                        setAiStatus(`Status: ${pollData.status}`);
                    }
                } catch (e: any) {
                    clearInterval(interval);
                    setAiProcessing(false);
                    toast({title: "Error", description: 'Polling error: ' + e.message, variant: "default" });
                }
            }, 3000);
        } else {
            setAiProcessing(false);
            toast({title: "Error", description: data.message || 'Error initing task', variant: "default" });
        }
    } catch(e) {
        setAiProcessing(false);
        toast({title: "Error", description: 'Network error starting AI task', variant: "default" });
    }
  };

  const repliesRedux = useSelector((state: RootState) => state.replies.replies);
  const isLoadingReplies = useSelector((state: RootState) => state.replies.isLoadingReplies);
  const handleVideoTime = (duration: number) => {
    try {
      const safeDuration = (duration && isFinite(duration) && duration > 0) ? duration : 0;
      const requiredTime = safeDuration > 0 ? (safeDuration * 0.1) : 5;
      if (cumulativeWatchTimeRef.current >= requiredTime && !viewRegistered) {
        setViewRegistered(true);

        if (!isScrubbingRef.current) {
          console.log("Adding new view");
          register_view(post.author, post.permlink, recently_viewed, updateRecentlyViewed, user);
        }
      }
    } catch (error) {
      handleVideoError(error);
    } 

  };
  const onTimeUpdate = () => {
    if (videoRef.current && !isScrubbingRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      
      const delta = currentTime - lastTimeUpdateRef.current;
      if (delta > 0 && delta < 3.0) {
        cumulativeWatchTimeRef.current += delta;
      }
      lastTimeUpdateRef.current = currentTime;

      if (duration && isFinite(duration)) {
        const percent = (currentTime / duration) * 100;
        setProgress(percent);
      }
      handleVideoTime(duration || 0);

      // Subtitles sync
      if (showSubtitles && parsedCues.length > 0) {
        const cue = parsedCues.find(c => currentTime >= c.start && currentTime <= c.end);
        if (cue) {
          if (currentSubtitle !== cue.text) setCurrentSubtitle(cue.text);
        } else {
          if (currentSubtitle !== "") setCurrentSubtitle("");
        }
      }
    }
  };
  const formatTime = (seconds: number) => {
    if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const updateProgressFromEvent = (e: React.PointerEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => {
    if (progressRef.current && videoRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      let clickX = e.clientX - rect.left;
      clickX = Math.max(0, Math.min(clickX, rect.width));
      const percentage = (clickX / rect.width) * 100;
      const duration = videoRef.current.duration;
      const newTime = (percentage / 100) * duration;
      if (isFinite(newTime)) {
        videoRef.current.currentTime = newTime;
        setProgress(percentage);
        setScrubTimeText(`${formatTime(newTime)} / ${formatTime(duration)}`);
      }
    }
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Prevent default to avoid selection issues while dragging
    e.preventDefault();
    e.stopPropagation();
    isScrubbingRef.current = true;
    setIsScrubbing(true);
    updateProgressFromEvent(e);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isScrubbingRef.current) {
      e.preventDefault();
      e.stopPropagation();
      updateProgressFromEvent(e);
    }
  };

  const handlePointerUpOrLeave = () => {
    if (isScrubbingRef.current) {
      isScrubbingRef.current = false;
      setIsScrubbing(false);
    }
  };

  const handleVideoError = (_e?: any) => {
    if (!cacheBypass && activeFinalUrl.includes('/stream/')) { setCacheBypass(true); return; }
    const video = videoRef.current;
    let msg = "Error loading video.";

    if (video && video.error) {
      const error = video.error;
      switch (error.code) {
        case 1: msg = "Playback aborted."; break;
        case 2: msg = "Network error. Check your connection or gateway."; break;
        case 3: msg = "Video decoding failed."; break;
        case 4: msg = "Video not found (404) or format not supported."; break;
      }
      console.warn(`[VideoFeed] Error ${error.code}: ${msg}`, activeFinalUrl);
    } else {
      console.warn("❌ Error loading video:", _e);
    }

    if (!failedGatewaysRef.current.includes(gateway)) failedGatewaysRef.current.push(gateway);
    const r2 = mediaConfiguration.r2PublicBaseUrl;
    if (gateway !== r2 && r2 && !failedGatewaysRef.current.includes(r2)) {
      if (!pendingSourceSeek.current && activeCidPath) pendingSourceSeek.current = { cid: activeCidPath.cid, time: video?.currentTime || 0 };
      setGateway(r2);
      setCacheBypass(false);
      setHasError(false);
      updateAppSettings({ ...app_settings, gateway: r2 });
      return;
    }
    setHasError(true);
    setErrorMessage(msg);
    setIsLoading(false);
  };

  const cycleGateway = async () => {
    if (!dynamicGateways.length) return;
    const current = preferredGateway(gateway);
    const candidates = dynamicGateways.filter(source => source !== current);
    const ipfsReady = activeCidPath && candidates.some(source => source !== mediaConfiguration.r2PublicBaseUrl)
      ? await ipfsAvailability(activeCidPath.cid) : false;
    const next = candidates.find(source => source === mediaConfiguration.r2PublicBaseUrl || ipfsReady);
    if (!next) {
      toast({ title: 'No other source available', description: 'Retry this video or move to the next one.' });
      return;
    }
    selectSource(next);
    setHasError(false);
    setErrorMessage('');
    setIsLoading(true);
  };


  useEffect(() => {
    try {
      if (post && recently_viewed) {
        const viewedList = recently_viewed;
        const postUrl = `/@${post.author}/${post.permlink}`;
        const today = new Date().toISOString().slice(0, 10);
        let alreadyViewed = false;

        if (viewedList) {
          alreadyViewed = viewedList.find(
            (view) => view.url === postUrl && view.date === today
          );
        }

        if (alreadyViewed) {
          setViewRegistered(true);
        }
      }
    } catch (error) {
      handleVideoError(error);
    }
  }, [recently_viewed, post]);

  useEffect(() => {
    setLocalReplies(repliesRedux || []);
  }, [repliesRedux]);

  useEffect(() => {
    if (post?.author && post?.permlink) {
      dispatch(fetchReplies(post.author, post.permlink));
    }
  }, [post?.author, post?.permlink, dispatch]);

  useEffect(() => {
    setViewRegistered(false);
    cumulativeWatchTimeRef.current = 0;
    lastTimeUpdateRef.current = 0;
  }, [activeIndex, activeUrl]);


  useEffect(() => {
    // Reset AI states when switching video
    setAiTranscription(null);
    setAiLanguage(null);
    setAiSrt(null);
    setAiSrtEn(null);
    setActiveSubTrack('original');
    setAiLanguageLabel('');
    setAiProcessing(false);
    setAiStatus('');
    setPostSheetTab('post');
    setTranscriptionViewMode('text');
    // Reset share inline state
    setShowShareConfirm(false);
    setHasSharedVideo(false);

    // Sync gateway when activeIndex changes if needed
    const postToSync = posts[activeIndex];
    if (!postToSync) return;

    try {
      let metadata = (typeof postToSync.json_metadata !== "string" || !postToSync.json_metadata || postToSync.json_metadata.trim() === "undefined") ? {} : JSON.parse(postToSync.json_metadata);
      let url = extractIPFSLink(postToSync.body, metadata) || metadata?.video;
      const cid = parseMediaReference(url)?.cid;

      if (cid) {
          get_ipfs_metadata(cid).then((cache: any) => {
              if (cache && cache.transcription && cache.transcription.trim() !== "") {
                  setAiTranscription(cache.transcription);
                  if (cache.language) {
                      setAiLanguage(cache.language);
                      setAiLanguageLabel(LANG_MAP[cache.language] || cache.language.toUpperCase());
                  }
                  if (cache.srt_content) setAiSrt(cache.srt_content);
                  if (cache.srt_content_en) setAiSrtEn(cache.srt_content_en);
              }
          }).catch((e: any) => console.log("Error caching IPFS", e));
      }

      setGateway(preferredGateway(app_settings?.gateway));
      setCacheBypass(false);
    } catch (e) {
      console.warn("Error syncing gateway", e);
    }
  }, [activeIndex, activeUrl]);

  const parseSRT = (srt: string) => {
    try {
      const cues: {start: number, end: number, text: string}[] = [];
      const blocks = srt.trim().split(/\n\s*\n/);
      
      const parseTime = (timeStr: string) => {
        const parts = timeStr.split(',');
        const hms = parts[0].split(':').map(Number);
        const ms = Number(parts[1] || 0) / 1000;
        return hms[0] * 3600 + hms[1] * 60 + hms[2] + ms;
      };

      for (const block of blocks) {
        const lines = block.split('\n');
        if (lines.length >= 2) {
          const timeLine = lines.find(l => l.includes('-->'));
          if (timeLine) {
            const times = timeLine.match(/(\d{2}:\d{2}:\d{2},\d{3}) --> (\d{2}:\d{2}:\d{2},\d{3})/);
            if (times) {
              const start = parseTime(times[1]);
              const end = parseTime(times[2]);
              const textLines = lines.slice(lines.indexOf(timeLine) + 1);
              const text = textLines.join(' ').replace(/<[^>]*>/g, '').trim(); // Remove HTML tags
              if (text) cues.push({ start, end, text });
            }
          }
        }
      }
      return cues;
    } catch (e) {
      console.error("Error parsing SRT", e);
      return [];
    }
  };

  const handleDownloadVideo = async () => {
      if (!user) { toggleLoginModal(); return; }
    const activePost = posts[activeIndex];
    const cid = getCIDPath(extractIPFSLink(activePost.body, JSON.parse(activePost.json_metadata || "{}")))?.cid;

    if (!cid) {
      alert("This video cannot be downloaded (IPFS CID not found).");
      return;
    }

    setIsDownloading(true);
    setDownloadStatus("Starting...");

    try {
      const res = await download_video(cid, user || "guest");
      if (res && res.task_id) {
        setDownloadStatus("Queued...");
        const interval = setInterval(async () => {
          try {
            const poll = await backendFetch(`${aiBackend}/api/v1/task/${res.task_id}`);
            const data = await poll.json();

            if (data.status === 'completed') {
              setDownloadStatus("Ready!");
              setIsDownloading(false);
              clearInterval(interval);
              
              // Trigger actual browser download
              const link = document.createElement('a');
              link.href = await get_ai_download_url(data.result.filename);
              link.setAttribute('download', data.result.filename);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              setTimeout(() => setDownloadStatus(""), 3000);
            } else if (data.status === 'failed') {
              setIsDownloading(false);
              setDownloadStatus("Error");
              alert("Download failed: " + (data.error || "Unknown error"));
              clearInterval(interval);
            } else {
              setDownloadStatus(data.status === 'processing' ? "Processing..." : "Queued...");
            }
          } catch (e) {
            console.error("Polling error", e);
          }
        }, 3000);
      } else {
        setIsDownloading(false);
        setDownloadStatus("Error");
      }
    } catch (e) {
      setIsDownloading(false);
      setDownloadStatus("Error");
      console.error(e);
    }
  };


  useEffect(() => {
    const activeSrt = activeSubTrack === 'en' ? aiSrtEn : aiSrt;
    if (activeSrt) {
      setParsedCues(parseSRT(activeSrt));
      setCurrentSubtitle("");
      setShowSubtitles(true); // Active by default when subs are found
    } else {
      setParsedCues([]);
      setCurrentSubtitle("");
    }
  }, [aiSrt, aiSrtEn, activeSubTrack]);

  // All hooks must run even while the asynchronous feed is empty.
  if (posts.length === 0) return <div className="feed-empty">No videos</div>;

  return (
    <div
      className="video-feed speem-video-feed"
      ref={containerRef}
      role="region"
      aria-label="Video feed. Scroll or use the arrow keys to change videos."
      tabIndex={0}
      onKeyDown={event => {
        if ((event.target as HTMLElement).closest('button,a,input,textarea,select,[contenteditable="true"]')) return;
        if (event.key === 'ArrowDown') { event.preventDefault(); handleNext(); }
        if (event.key === 'ArrowUp') { event.preventDefault(); handlePrev(); }
      }}
    >
      {posts.map((postItem, index) => {
        const isCurrentlyActive = index === activeIndex;
        const isNext = index === activeIndex + 1;
        const isNextNext = index === activeIndex + 2;
        const isNearby = Math.abs(index - activeIndex) <= 3;

        // --- Data extraction for the current post ---
        const post = postItem;
        let metadata: any = {};
        try {
          metadata = (typeof post.json_metadata !== "string" || !post.json_metadata || post.json_metadata.trim() === "undefined") 
            ? {} 
            : JSON.parse(post.json_metadata);
        } catch (error) {
          console.log("While parsing metadata", error);
        }

        const url = extractIPFSLink(post.body, metadata) || metadata?.video;
        const cidPath = getCIDPath(url);
        const summary = extractBodySummary(post.body, false);
        const payouTimeString = getTimeToExpiration(post.created);

        let tags: string[] = [];
        try {
          tags = metadata?.tags || [];
        } catch (error) {
          console.log("While building tags", error);
        }

        const finalUrl = mediaUrl(url, index === activeIndex ? gateway : preferredGateway(app_settings?.gateway), index === activeIndex && !cacheBypass);

        // Other UI data
        const postUrl = `/@${post.author}/${post.permlink}`;
        const view = views.find((v: any) => v.url === postUrl);
        const viewsCount = view ? (view as any).count : 0;
        const plan = userPlans.find((u: any) => u.username === post.author);
        const isPlus = (plan as any)?.plan_type?.includes("plus") || false;
        const imageLink = `https://steemitimages.com/u/${post.author}/avatar`;

        // Logic for preloading siblings (removed redundant nextFinalUrl logic as we use mapping)

        return (
          <div
            className={`video-item ${isCurrentlyActive ? 'active' : ''} ${!isNearby ? 'placeholder' : ''}`}
            key={`video-item-${index}`} // Stable key is critical for IntersectionObserver
            data-index={index}
            ref={(el) => (itemRefs.current[index] = el)}
          >
            {isNearby ? (
              <>
                {isCurrentlyActive && showOptionsSheet && (
                  <div 
                    className={`video-options-sheet ${closingOptionsSheet ? "sheet-slide-out" : "sheet-slide-in"}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="options-sheet-header">
                       <span>More Options</span>
                       <button className="close-options" onClick={closeMoreOptions}>✕</button>
                    </div>
                    <div className="options-sheet-body">
                         {/* ── Share item ── */}
                         {!showShareConfirm ? (
                           <div
                             className="option-item"
                             onClick={(e) => { e.stopPropagation(); setShowShareConfirm(true); }}
                           >
                             <div className="option-action-btn-wrap">
                               <i className="fas fa-share option-icon" />
                               <span className="option-text">Share Video</span>
                             </div>
                             <i className="fas fa-chevron-right" style={{ opacity: 0.4, fontSize: '0.8rem' }} />
                           </div>
                         ) : (
                           <div className="option-item option-item-confirm" onClick={(e) => e.stopPropagation()}>
                             <div style={{ flex: 1 }}>
                               <p className="option-text mb-1" style={{ fontWeight: 600 }}>Share this video?</p>
                               <p style={{ fontSize: '0.8rem', opacity: 0.65, margin: 0 }}>It will appear on your profile feed.</p>
                             </div>
                             <div className="d-flex" style={{ gap: '8px', marginLeft: '12px', flexShrink: 0 }}>
                               <button
                                 className="btn btn-sm btn-light"
                                 style={{ borderRadius: '10px', minWidth: '60px' }}
                                 onClick={(e) => { e.stopPropagation(); setShowShareConfirm(false); }}
                               >
                                 Cancel
                               </button>
                               <button
                                 className="btn btn-sm btn-speem"
                                 style={{ borderRadius: '10px', minWidth: '60px' }}
                                 disabled={isSharingVideo}
                                 onClick={(e) => { e.stopPropagation(); handleInlineShare(); }}
                               >
                                 {isSharingVideo ? <span className="spinner dark small m-0" /> : 'Confirm'}
                               </button>
                             </div>
                           </div>
                         )}

                        {aiSrt && (
                          <div className="option-item" onClick={(e) => { e.stopPropagation(); setShowSubtitles(!showSubtitles); }}>
                             <div className="option-action-btn-wrap">
                                <div className="d-flex align-items-center">
                                    {(aiSrtEn && aiLanguage !== 'en') && (
                                        <select 
                                            className="pro-lang-select mr-2" 
                                            value={activeSubTrack}
                                            onChange={(e) => setActiveSubTrack(e.target.value as any)}
                                        >
                                            <option value="original" style={{ background: '#000' }}>{aiLanguageLabel || 'Original'}</option>
                                            <option value="en" style={{ background: '#000' }}>English</option>
                                        </select>
                                    )}
                                    <i 
                                        className={`fas fa-closed-captioning option-icon ${showSubtitles ? 'text-speem' : ''}`} 
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => { e.stopPropagation(); setShowSubtitles(!showSubtitles); }}
                                    />
                                </div>
                                <span className="option-text">Subtitles</span>
                             </div>
                             <div className={`option-toggle-indicator ${showSubtitles ? 'active' : ''}`}></div>
                          </div>
                        )}

                        {/* ── Copy Link item ── */}
                        <div className="option-item" onClick={(e) => { e.stopPropagation(); handleCopyLink(); }}>
                          <div className="option-action-btn-wrap">
                            <i className="fas fa-link option-icon" />
                            <span className="option-text">Copy Link</span>
                          </div>
                          <i className="fas fa-copy" style={{ opacity: 0.4, fontSize: '0.8rem' }} />
                        </div>

                        {/* ── Download item ── */}
                        <div className="option-item" onClick={(e) => { e.stopPropagation(); if (!isDownloading) handleDownloadVideo(); }}>
                          <div className="option-action-btn-wrap">
                            <i className={`fas ${isDownloading ? 'fa-spinner fa-spin' : 'fa-download'} option-icon`} />
                            <span className="option-text">{isDownloading ? `Downloading (${downloadStatus})` : 'Download Video'}</span>
                          </div>
                          {downloadStatus === "Ready!" ? <i className="fas fa-check text-success" /> : <i className="fas fa-chevron-right" style={{ opacity: 0.4, fontSize: '0.8rem' }} />}
                        </div>
                    </div>
                  </div>
                )}

                {isCurrentlyActive && showComments && (
                  <div
                    className={`video-comments ${closing ? "slide-out" : "slide-in"}`}
                    role="region" aria-label="Comments"
                    onWheel={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                  >
                    <div className="comments-content">
                      <h2 className="comments-title">Comments</h2>
                      <button type="button" className="close-comments" aria-label="Close comments" onClick={closeComments}><ShellIcon name="close" size={18} /></button>
                    </div>
                    <div className="video-comments-replies-wrap">
                      <VideoReplies
                        post={post}
                        userPlans={userPlans}
                        localReplies={localReplies}
                        setLocalReplies={setLocalReplies}
                        isLoadingReplies={isLoadingReplies}
                        onLogin={toggleLoginModal}
                        onError={() => { }}
                      />
                    </div>
                    <div className="comment-input-bar">
                      <ReplyEditor
                        depth={1}
                        type="simple"
                        author={post.author}
                        permlink={post.permlink}
                        parent_author={post.author}
                        parent_permlink={post.permlink}
                        onNewError={() => { }}
                        onLogin={toggleLoginModal}
                        IpfsAccount={post.author}
                      />
                    </div>
                  </div>
                )}

                <div 
                  className="video-layout" 
                  onClick={togglePlay}
                  onTouchStart={handleSwipeStart}
                  onTouchMove={handleSwipeMove}
                  onTouchEnd={() => handleSwipeEnd(post.author)}
                >
                  {isCurrentlyActive && showPostSheet && (() => {
                    let renderedBody = null;
                    try {
                      let bodyText = post.body;
                      if (url) {
                        const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                        bodyText = bodyText
                          .replace(new RegExp(`\\[IPFS Video\\]\\(${escapedUrl}\\)`, 'gi'), '')
                          .replace(new RegExp(`<a[^>]+href=["']${escapedUrl}["'][^>]*>.*?<\/a>`, 'gi'), '')
                          .replace(url, '')
                          .replace(/<iframe.*?<\/iframe>/g, '')
                          .replace(/<img[^>]*src=["']\s*["'][^>]*>/gi, '');
                      }
                      renderedBody = renderHTMLSafe(replaceIPFSVideoLinks(renderer.render(bodyText)));
                    } catch (e) {
                      renderedBody = <span className="text-muted">Could not render post content.</span>;
                    }
                    return (
                      <div
                        className={`video-post-sheet ${closingPostSheet ? "sheet-slide-out" : "sheet-slide-in"}`}
                        onClick={(e) => e.stopPropagation()}
                        onWheel={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        onTouchMove={(e) => e.stopPropagation()}
                        onTouchEnd={(e) => e.stopPropagation()}
                      >
                        <div className="video-post-sheet-inner">
                          <div className="comments-content" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span style={{ fontWeight: 600, fontSize: "1rem", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {post.title}
                            </span>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "10px" }}>
                              {(user && user === post.author) && (
                                <button
                                  className="close-comments"
                                  style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center" }}
                                  onClick={() => setIsEditOpen(true)}
                                  title="Edit Post"
                                >
                                  <i className="fas fa-pen" style={{ color: "var(--speem)", fontSize: "0.9rem" }} />
                                </button>
                              )}
                              <button 
                                className="close-comments" 
                                style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center" }} 
                                onClick={closePostSheet}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                          
                            <div className="menu-tabs-compact">
                              <button
                                className={classnames('tab-button', { active: postSheetTab === 'post' })}
                                onClick={() => setPostSheetTab('post')}
                              >
                                Post
                              </button>
                              {cidPath?.cid && (
                                <button
                                  className={classnames('tab-button', { active: postSheetTab === 'transcription' })}
                                  onClick={() => setPostSheetTab('transcription')}
                                >
                                  Transcription 
                                  <i className="ai-icon-gradient ml-1"></i>
                                </button>
                              )}
                            </div>

                          <div className="video-post-sheet-body">
                            {postSheetTab === 'post' ? (
                              <div className="body-content-wrap">
                                <div className="body-content">{renderedBody}</div>
                                <div className="text-center pb-4">
                                  <a
                                    href={`/p/${post.author}/${post.permlink}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn btn-sm mt-3 d-inline-block"
                                    style={{ background: "var(--speem)", color: "var(--white)", borderRadius: "8px" }}
                                  >
                                    <i className="fas fa-external-link-alt" /> Open full post
                                  </a>
                                </div>
                              </div>
                            ) : (
                              <div className="transcription-content-wrap" style={{padding:'1rem'}}>
                                {aiTranscription ? (
                                  <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                      <div>
                                        {aiLanguage && <span className="badge badge-info mr-2">Language: {aiLanguage.toUpperCase()}</span>}
                                        <span className="badge badge-light border text-dark">AI Generated</span>
                                      </div>
                                      
                                      {aiSrt && (
                                        <div className="script-mode-toggle">
                                          <button 
                                            className={`btn btn-sm ${transcriptionViewMode === 'text' ? 'active' : ''}`}
                                            onClick={() => setTranscriptionViewMode('text')}
                                          >Text</button>
                                          <button 
                                            className={`btn btn-sm ${transcriptionViewMode === 'srt' ? 'active' : ''}`}
                                            onClick={() => setTranscriptionViewMode('srt')}
                                          >Subtitles</button>
                                        </div>
                                      )}
                                    </div>
                                    
                                    <div className="transcription-script-view mb-3"
                                      onWheel={(e) => e.stopPropagation()}
                                      onTouchStart={(e) => e.stopPropagation()}
                                      onTouchMove={(e) => e.stopPropagation()}
                                      onTouchEnd={(e) => e.stopPropagation()}
                                    >
                                      {transcriptionViewMode === 'srt' && aiSrt ? aiSrt.replace(/,\d{3}/g, '') : aiTranscription}
                                    </div>
                                    
                                    <div className="text-muted small text-center mt-2 pb-4">
                                      <i className="fas fa-exclamation-triangle mr-1"></i>
                                      Warning: This transcription was generated automatically by AI and may contain inaccuracies.
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center py-5 px-3">
                                      <i className="ai-icon-muted mb-3 opacity-5" style={{fontSize: '1em'}}></i>
                                      <h5 className="text-muted">No transcription available</h5>
                                      <p className="text-muted mb-4 small">This video doesn't have an AI transcription yet.</p>
                                      
                                      {(() => {
                                        const plan = userPlans.find((u: any) => u.username === user);
                                        let isCurrentUserPlus = plan?.plan_type?.includes("plus") || false;
                                        
                                        if (!user) {
                                          return <button className="btn btn-sm btn-primary" onClick={toggleLoginModal}>Login to Use AI</button>;
                                        }
                                        
                                        if (isCurrentUserPlus) {
                                            return (
                                              <div className="d-flex flex-column align-items-center">
                                                {aiProcessing ? (
                                                  <div className="ai-status-card animate__animated animate__fadeIn">
                                                    <div className="ai-pulse-indicator"></div>
                                                    <span className="text-speem font-weight-bold tiny-text mt-1">{aiStatus}...</span>
                                                    <Spinner size="sm" className="opacity-5" color="secondary" />
                                                  </div>
                                                ) : (
                                                  <button 
                                                    className="btn-ai-action" 
                                                    onClick={generateTranscriptionFromIPFS}
                                                  >
                                                      Generate Transcription 
                                                      <i className="ai-icon-white ml-2"></i>
                                                  </button>
                                                )}
                                              </div>
                                            );
                                        } else {
                                            return (
                                                <div className="alert alert-secondary text-sm d-inline-block p-2">
                                                    <i className="fas fa-lock text-warning mr-2"></i>
                                                    Requires <strong>Speak Plus</strong> to generate new AI transcripts.
                                                </div>
                                            );
                                        }
                                      })()}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {(isCurrentlyActive || isNext || isNextNext) ? (
                    <video
                      key={`video-element-${index}`}
                      ref={index === activeIndex ? videoRef : null}
                      className={`video-element ${isCurrentlyActive ? 'active' : 'preload'}`}
                      src={finalUrl}
                      autoPlay={isCurrentlyActive}
                      playsInline
                      loop
                      muted={isCurrentlyActive ? isMuted : true}
                      preload={(isCurrentlyActive || isNext) ? "auto" : "metadata"}
                      controls={false}
                      crossOrigin="anonymous"
                      style={{ 
                        display: isCurrentlyActive ? 'block' : 'none'
                      }}
                      onLoadedMetadata={isCurrentlyActive ? event => {
                        const pending = pendingSourceSeek.current;
                        if (pending?.cid === cidPath?.cid) {
                          const video = event.currentTarget;
                          video.currentTime = Number.isFinite(video.duration) ? Math.min(pending.time, Math.max(0, video.duration - 0.05)) : pending.time;
                          pendingSourceSeek.current = null;
                        }
                      } : undefined}
                      onTimeUpdate={isCurrentlyActive ? onTimeUpdate : undefined}
                      onPlay={isCurrentlyActive ? () => { setIsPlaying(true); setIsLoading(false); if (stalledTimerRef.current) clearTimeout(stalledTimerRef.current); } : undefined}
                      onPause={isCurrentlyActive ? () => { setIsPlaying(false); } : undefined}
                      onWaiting={isCurrentlyActive ? () => { 
                        setIsLoading(true); 
                        console.log("[VideoFeed] Video waiting — loading...");
                      } : undefined}
                      onCanPlay={isCurrentlyActive ? () => { setIsLoading(false); if (stalledTimerRef.current) clearTimeout(stalledTimerRef.current); } : undefined}
                      onStalled={isCurrentlyActive ? handleStalled : undefined}
                      onError={isCurrentlyActive ? (e) => handleVideoError(e) : undefined}
                    />
                  ) : null}

                  {!isCurrentlyActive && (
                    <div className="video-placeholder-container" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
                      {/* Show a thumbnail if available, or just a dark background */}
                      <img 
                        src={`https://steemitimages.com/${metadata?.image || metadata?.thumb || ''}`}
                        alt=""
                        className="video-element-placeholder"
                        style={{ 
                          width: 'auto', 
                          height: '100%', 
                          opacity: 0.3, 
                          objectFit: 'cover',
                          aspectRatio: '9/16'
                        }}
                        onError={(e) => (e.currentTarget.style.display = 'none')}
                      />
                    </div>
                  )}

                  {isCurrentlyActive && (
                    <>
                      {isLoading && <span className="spinner video-loading"></span>}
                      {!isPlaying && !isLoading && (
                        <img src="/assets/img/play.png" alt="Play" className="video-play-icon" />
                      )}

                      <div
                        className="progress-bar-video"
                        ref={progressRef}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUpOrLeave}
                        onPointerLeave={handlePointerUpOrLeave}
                        onPointerCancel={handlePointerUpOrLeave}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isScrubbing && <div className="scrub-time-tooltip">{scrubTimeText}</div>}
                        <div
                          className="progress-bar-video-fill"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>

                      {hasError && (
                        <div className="video-error-placeholder no-absolute-center scroll-safe-overlay">
                          <div className="error-content">
                            <i className="fas fa-exclamation-triangle error-icon" />
                            <p className="error-text">{errorMessage}</p>
                            <div className="d-flex flex-column align-items-center">
                              <div className="d-flex justify-content-center mb-3">
                                <button
                                  className="btn btn-sm btn-outline-light mr-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setHasError(false);
                                    failedGatewaysRef.current = [];
                                    videoRef.current?.load();
                                    if (videoRef.current) safePlay(videoRef.current);
                                  }}
                                >
                                  <i className="fas fa-redo mr-1" /> Retry
                                </button>
                                <button
                                  className="btn btn-sm btn-speem"
                                  onClick={(e) => { e.stopPropagation(); cycleGateway(); }}
                                >
                                  <i className="fas fa-exchange-alt mr-1" /> Next gateway
                                </button>
                              </div>
                              <button
                                className="btn btn-sm btn-link text-white opacity-8"
                                onClick={(e) => { e.stopPropagation(); handleNext(true); }}
                              >
                                Skip video <i className="fas fa-chevron-down ml-1" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}


                      <div className="feed-source-control"><VideoSourceMenu cid={cidPath?.cid} value={gateway} onChange={selectSource} /></div>
                      <VideoVolumeControl muted={isMuted} volume={volume} onToggleMute={toggleMute} onVolumeChange={value => { setVolume(value); setIsMuted(value === 0); }} />

                      <span className="video-views"><i className="fas fa-eye" /> {viewsCount || 0}</span>

                      <div className="video-overlay">
                        <h3 className="video-title mb-0" onClick={(e) => e.stopPropagation()}>
                          <span onClick={openPostSheet} role="button">{post.title}</span>
                        </h3>
                        <div className="video-summary" onClick={(e) => e.stopPropagation()}>
                          <b><Link to={`/u/${post.author}`} className="video-description mb-1 d-inline-block">{post.author}</Link></b>
                          {isPlus && <VerifiedIcon size={18} className="color-speem ml-1" />}
                          <span className="text-muted ml-2" style={{ fontSize: '0.8rem', opacity: 0.7 }}>• {getTimeAgo(post.created)}</span>
                          <div className="video-description-text" onClick={openPostSheet}>{summary}</div>
                          <span className="read-more-btn" onClick={openPostSheet}>Read more</span>
                          <div className="mt-1">
                            {tags.slice(0, 3).map((tag, i) => (
                              <Link key={i} to={`/new/${tag}`} className="video-tag">#{tag} </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {isCurrentlyActive && showSubtitles && currentSubtitle && (
                    <div className="video-subtitle-overlay animate__animated animate__fadeIn">
                      <span className="subtitle-text">{currentSubtitle}</span>
                    </div>
                  )}
                </div>

                {isCurrentlyActive && (
                  <>
                    <div className="swipe-buttons">
                      <div className="swipe-action-item avatar-container">
                        <Link to={"/u/" + post.author}>
                          <img className="swipe-btn" src={imageLink} alt="Avatar" />
                        </Link>
                        <div className="follow-buble-wrapper" onClick={(e) => e.stopPropagation()}>
                          <FollowButton author={post.author} className="follow-buble" />
                        </div>
                      </div>
                      <VoteButton
                        post={post}
                        author={post.author}
                        permlink={post.permlink}
                        reward_fund={post.reward_fund}
                        median_price={post.median_price}
                        setLocalReplies={setLocalReplies}
                        onLogin={toggleLoginModal}
                        className='swipe-btn'
                        position="right"
                        onNewError={() => { }}
                      />
                      <RewardsButton
                        payoutTimeString={payouTimeString}
                        post={post}
                        className='swipe-rewards'
                        position="right"
                        onNewError={() => { }}
                      />
                      <div className="swipe-action-item">
                        <button className="swipe-btn" aria-label="Open comments" title="Comments" onClick={openComments}><i className="fas fa-comment" /></button>
                        <span className="swipe-action-text">{localReplies.length}</span>
                      </div>
                      <div className="swipe-action-item">
                        <button className="swipe-btn" aria-label="More video options" title="More options" onClick={openMoreOptions}>
                          <i className="fas fa-ellipsis-h" />
                        </button>
                        {/* <span className="swipe-action-text">More</span> */}
                      </div>

                      {index === posts.length - 1 && (loadingMore || moreError) && <div className="feed-load-status" role="status">{loadingMore ? 'Loading…' : <button type="button" onClick={() => void loadNextPage()}>Retry loading videos</button>}</div>}
                      <button className="swipe-btn scroll-btn" aria-label="Previous video" title="Previous video" onClick={handlePrev} disabled={index === 0}><i className="fas fa-chevron-up" /></button>
                      <button className="swipe-btn scroll-btn" aria-label={loadingMore && index === posts.length - 1 ? "Loading more videos" : "Next video"} title="Next video" onClick={() => handleNext()} disabled={index === posts.length - 1 && (!hasMore || loadingMore)}><i className="fas fa-chevron-down" /></button>
                    </div>
                  </>
                )}
              </>
            ) : null}
          </div>
        );
      })}

      {isEditOpen && posts[activeIndex] && (
        <ContentEditPostModal
          isShowing={isEditOpen}
          isComment={posts[activeIndex].depth > 0}
          hide={() => setIsEditOpen(false)}
          title={posts[activeIndex].title}
          description={posts[activeIndex].body}
          tags={(() => {
            try {
              return JSON.parse(posts[activeIndex].json_metadata).tags || [];
            } catch (e) {
              return [];
            }
          })()}
          post={posts[activeIndex]}
          onSuccess={(updatedPost: any) => {
            setIsEditOpen(false);
            setPosts((prevPosts: any[]) =>
              prevPosts.map((p: any) =>
                p.permlink === updatedPost.permlink ? updatedPost : p
              )
            );
          }}
        />
      )}
    </div>
  );
}